import React, { useEffect, useRef, useState } from "react";
import { XMarkIcon, MapPinIcon, ClockIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { GetCommanWay } from "API/commonApi";

export interface TrainTrackingModalProps {
  isOpen: boolean;
  onClose: () => void;
  trainNo: string;
  trainName?: string;
  defaultDate?: string;
}

interface StationData {
  stationName: string;
  distance: string;
  actualTime: string;
  scheduledTime: string;
  delay: string;
  platform: string;
  halt: string;
  isCurrent: boolean;
  isSource: boolean;
  isDestination: boolean;
}

interface TrackingResponse {
  success: boolean;
  train_name: string;
  message: string;
  updated_time: string;
  data: {
    is_current_station: boolean;
    station_name: string;
    distance: string;
    timing: string;
    delay: string;
    platform: string;
    halt: string;
  }[];
}

const TrainTrackingModal: React.FC<TrainTrackingModalProps> = ({ 
  isOpen, 
  onClose, 
  trainNo, 
  trainName = "",
}) => {
  const [trackingData, setTrackingData] = useState<TrackingResponse | null>(null);
  const [stations, setStations] = useState<StationData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const currentStationRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollToCurrentStation = () => {
    setTimeout(() => {
      if (currentStationRef.current && scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const element = currentStationRef.current;
        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const scrollTop = element.offsetTop - container.offsetTop - (containerRect.height / 2) + (elementRect.height / 2);
        container.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
      }
    }, 100);
  };

  useEffect(() => {
    if (isOpen && trainNo) {
      fetchTrackingData();
    }
    return () => {
      setTrackingData(null);
      setStations([]);
      setError(null);
    };
  }, [isOpen, trainNo]);

  const parseTiming = (timing: string): { actual: string; scheduled: string } => {
    if (timing === "Source" || timing === "Destination" || !timing) {
      return { actual: "", scheduled: "" };
    }
    // Format: "HH:MMHH:MM" (actual 5 chars + scheduled 5 chars)
    if (timing.length === 10 && timing[2] === ':' && timing[7] === ':') {
      return {
        actual: timing.substring(0, 5),
        scheduled: timing.substring(5, 10),
      };
    }
    // Fallback: return as-is
    return { actual: timing, scheduled: "" };
  };

  const parseStations = (data: TrackingResponse): StationData[] => {
    return data.data.map((s) => {
      const { actual, scheduled } = parseTiming(s.timing);
      return {
        stationName: s.station_name,
        distance: s.distance,
        actualTime: actual,
        scheduledTime: scheduled,
        delay: s.delay,
        platform: s.platform,
        halt: s.halt,
        isCurrent: s.is_current_station,
        isSource: s.halt === "Source",
        isDestination: s.halt === "Destination",
      };
    });
  };

  const fetchTrackingData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data: any = await GetCommanWay(`/api/train?train_no=${trainNo}`, {}, {});
      if (data && data.data?.success && data.data?.data?.length > 0) {
        setTrackingData(data.data);
        setStations(parseStations(data.data));
        scrollToCurrentStation();
      } else {
        setError("No tracking data available for this train");
      }
    } catch (err) {
      setError("Failed to fetch tracking data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getDelayColor = (delay: string) => {
    if (!delay || delay === "Right Time" || delay === "0min") return "text-green-600";
    const mins = parseInt(delay);
    if (isNaN(mins)) return "text-gray-600";
    if (mins <= 5) return "text-green-600";
    if (mins <= 15) return "text-yellow-600";
    if (mins <= 30) return "text-orange-600";
    return "text-red-600";
  };

  const getDelayBg = (delay: string) => {
    if (!delay || delay === "Right Time" || delay === "0min") return "bg-green-100 text-green-700";
    const mins = parseInt(delay);
    if (isNaN(mins)) return "bg-gray-100 text-gray-600";
    if (mins <= 5) return "bg-green-100 text-green-700";
    if (mins <= 15) return "bg-yellow-100 text-yellow-700";
    if (mins <= 30) return "bg-orange-100 text-orange-700";
    return "bg-red-100 text-red-700";
  };

  const parseTimeToMinutes = (timeStr: string): number | null => {
    if (!timeStr || timeStr.length < 5) return null;
    const [h, m] = timeStr.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return null;
    return h * 60 + m;
  };

  const parseHaltMinutes = (halt: string): number => {
    if (!halt || halt === "-" || halt === "Source" || halt === "Destination") return 0;
    const match = halt.match(/(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };

  /** Compute day offsets for stations by detecting midnight crossings */
  const getDayOffsets = (stationList: StationData[]): number[] => {
    if (stationList.length === 0) return [];
    const offsets: number[] = [0];
    for (let i = 1; i < stationList.length; i++) {
      const prevTime = parseTimeToMinutes(stationList[i - 1].actualTime || stationList[i - 1].scheduledTime);
      const currTime = parseTimeToMinutes(stationList[i].actualTime || stationList[i].scheduledTime);
      // If time drops by more than 1 hour, we crossed midnight
      offsets.push(
        prevTime !== null && currTime !== null && currTime < prevTime - 60
          ? offsets[i - 1] + 1
          : offsets[i - 1]
      );
    }
    return offsets;
  };

  /** Returns which "day" we're currently on relative to the journey start */
  const getCurrentDayOffset = (stationList: StationData[], dayOffsets: number[]): number => {
    if (stationList.length === 0 || dayOffsets.length === 0) return 0;
    const firstMins = parseTimeToMinutes(stationList[0].actualTime || stationList[0].scheduledTime);
    if (firstMins === null) return 0;
    const now = new Date();
    const currentMins = now.getHours() * 60 + now.getMinutes();
    // If current time is significantly before departure (>6hrs), we're past midnight → day 1
    if (currentMins < firstMins && firstMins - currentMins > 360) return 1;
    return 0;
  };

  /** Returns whether the train is still physically at the given station */
  const isTrainStillAtStation = (station: StationData, stationDayOffset: number, currentDayOffset: number): boolean => {
    const now = new Date();
    const currentMins = now.getHours() * 60 + now.getMinutes();
    const currentAbsolute = currentMins + currentDayOffset * 1440;

    const timeStr = station.actualTime || station.scheduledTime;
    const stationMins = parseTimeToMinutes(timeStr);
    if (stationMins === null) return true;

    let departureMins: number;
    if (station.isSource) {
      departureMins = stationMins;
    } else {
      const haltMins = parseHaltMinutes(station.halt);
      departureMins = stationMins + haltMins;
    }
    const departureAbsolute = departureMins + stationDayOffset * 1440;

    return currentAbsolute <= departureAbsolute;
  };

  const getCurrentIndex = (stationList: StationData[], dayOffsets: number[], currentDayOffset: number): number => {
    // First check if API marks a station as current
    const apiCurrent = stationList.findIndex(s => s.isCurrent);
    if (apiCurrent !== -1) return apiCurrent;

    if (stationList.length === 0) return -1;

    const now = new Date();
    const currentMins = now.getHours() * 60 + now.getMinutes();
    const currentAbsolute = currentMins + currentDayOffset * 1440;

    // Find last station whose time is in the past
    for (let i = stationList.length - 1; i >= 0; i--) {
      const stationMins = parseTimeToMinutes(stationList[i].actualTime || stationList[i].scheduledTime);
      if (stationMins === null) continue;
      const stationAbsolute = stationMins + dayOffsets[i] * 1440;
      if (stationAbsolute <= currentAbsolute) {
        return i;
      }
    }
    return 0;
  };

  const formatDelay = (delay: string) => {
    if (!delay || delay === "Right Time" || delay === "0min") return "On Time";
    return `Late ${delay}`;
  };

  if (!isOpen) return null;

  const dayOffsets = getDayOffsets(stations);
  const currentDayOffset = getCurrentDayOffset(stations, dayOffsets);
  const currentIndex = getCurrentIndex(stations, dayOffsets, currentDayOffset);
  const trainAtStation = currentIndex >= 0 && stations[currentIndex]
    ? isTrainStillAtStation(stations[currentIndex], dayOffsets[currentIndex], currentDayOffset)
    : false;
  const progress = stations.length > 0 ? ((Math.max(0, currentIndex) + 1) / stations.length) * 100 : 0;
  const displayTrainName = trackingData?.train_name?.replace(/^\d+\s*/, '') || trainName;

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white px-4 py-2">
            <div className="flex justify-between items-center gap-4">
              {/* Left: Train info */}
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold truncate">{trainNo} - {displayTrainName}</h2>
                {trackingData && (
                  <p className="text-blue-100 text-xs truncate">{trackingData.message}</p>
                )}
              </div>
              
              {/* Center: Progress */}
              {stations.length > 0 && (
                <div className="flex-1 max-w-xs hidden sm:block">
                  <div className="flex justify-between text-[10px] text-blue-200 mb-0.5">
                    <span>{stations[0]?.stationName?.substring(0, 6)}</span>
                    <span>{Math.round(progress)}%</span>
                    <span>{stations[stations.length - 1]?.stationName?.substring(0, 6)}</span>
                  </div>
                  <div className="h-1.5 bg-blue-900 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-400 transition-all duration-500 rounded-full"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}
              
              {/* Right: Controls */}
              <div className="flex items-center gap-2">
                {trackingData?.updated_time && (
                  <span className="text-blue-200 text-[10px] hidden md:flex items-center gap-1">
                    <ClockIcon className="h-3 w-3" />
                    {trackingData.updated_time}
                  </span>
                )}
                <button onClick={onClose} className="p-1 hover:bg-blue-700 rounded-full transition-colors">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div ref={scrollContainerRef} className="overflow-y-auto max-h-[calc(90vh-120px)]">
            {loading && (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Fetching live status...</span>
              </div>
            )}

            {error && (
              <div className="flex flex-col items-center justify-center py-20 text-center px-4">
                <ExclamationTriangleIcon className="h-16 w-16 text-red-400 mb-4" />
                <p className="text-red-600 font-medium">{error}</p>
                <button 
                  onClick={fetchTrackingData}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            )}

            {!loading && !error && stations.length > 0 && (
              <div className="p-4 pl-6">
                <div className="relative">
                  {stations.map((station, index) => {
                    const isCurrent = index === currentIndex && trainAtStation;
                    const isInTransitAfter = index === currentIndex && !trainAtStation && index < stations.length - 1;
                    const isPassed = currentIndex >= 0 && index <= currentIndex;

                    return (
                      <div 
                        key={index} 
                        className="relative flex"
                        ref={isCurrent || isInTransitAfter ? currentStationRef : null}
                      >
                        {/* Timeline */}
                        <div className="flex flex-col items-center mr-4">
                          <div className={`w-4 h-4 rounded-full border-2 z-10 ${
                            isCurrent ? "bg-blue-600 border-blue-600 animate-pulse" :
                            isPassed ? "bg-green-500 border-green-500" :
                            "bg-white border-gray-300"
                          }`}>
                            {isCurrent && (
                              <div className="absolute -left-1 -top-1 w-6 h-6 bg-blue-400 rounded-full animate-ping opacity-50" />
                            )}
                          </div>
                          {index < stations.length - 1 && (
                            <div className={`w-0.5 flex-1 min-h-[60px] relative ${
                              isPassed ? "bg-green-500" : "bg-gray-200"
                            }`}>
                              {isInTransitAfter && (
                                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center">
                                  <span className="ml-4 whitespace-nowrap px-2 py-1 bg-blue-600 text-white text-[10px] rounded-full font-bold animate-pulse">
                                    🚂 In Transit
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Station Card */}
                        <div 
                          className={`flex-1 mb-3 p-3 rounded-lg border transition-all ${
                            isCurrent ? "bg-amber-50 border-amber-400 shadow-lg ring-2 ring-amber-300" :
                            isPassed ? "bg-green-50 border-green-200" :
                            "bg-gray-50 border-gray-200"
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-bold text-gray-900">{station.stationName}</span>
                                {station.platform && station.platform !== "-" && (
                                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                                    PF {station.platform}
                                  </span>
                                )}
                                {station.distance && station.distance !== "-" && (
                                  <span className="text-gray-400 text-xs">{station.distance}</span>
                                )}
                              </div>
                              
                              {/* Timing */}
                              <div className="mt-2 flex items-center gap-4 text-sm">
                                {station.isSource && (
                                  <div>
                                    <span className="text-gray-500">Dep: </span>
                                    <span className="font-medium">{station.scheduledTime || "-"}</span>
                                    {station.actualTime && (
                                      <>
                                        <span className="text-gray-400"> → </span>
                                        <span className={`font-medium ${getDelayColor(station.delay)}`}>
                                          {station.actualTime}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                )}
                                {station.isDestination && (
                                  <div>
                                    <span className="text-gray-500">Arr: </span>
                                    <span className="font-medium">{station.scheduledTime || "-"}</span>
                                    {station.actualTime && (
                                      <>
                                        <span className="text-gray-400"> → </span>
                                        <span className={`font-medium ${getDelayColor(station.delay)}`}>
                                          {station.actualTime}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                )}
                                {!station.isSource && !station.isDestination && (
                                  <>
                                    <div>
                                      <span className="text-gray-500">Sch: </span>
                                      <span className="font-medium">{station.scheduledTime || "-"}</span>
                                    </div>
                                    {station.actualTime && (
                                      <div>
                                        <span className="text-gray-500">Act: </span>
                                        <span className={`font-medium ${getDelayColor(station.delay)}`}>
                                          {station.actualTime}
                                        </span>
                                      </div>
                                    )}
                                    {station.halt && station.halt !== "-" && (
                                      <div>
                                        <span className="text-gray-500">Halt: </span>
                                        <span className="font-medium text-gray-700">{station.halt}</span>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Status indicator */}
                            <div className="ml-2 flex flex-col items-end gap-1">
                              {station.delay && (
                                <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${getDelayBg(station.delay)}`}>
                                  {formatDelay(station.delay)}
                                </span>
                              )}
                              {isCurrent && (
                                <span className="px-2 py-1 bg-amber-500 text-white text-xs rounded-full font-bold animate-pulse">
                                  Current
                                </span>
                              )}
                              {isInTransitAfter && (
                                <span className="text-[10px] text-green-600 font-medium">Departed</span>
                              )}
                              {station.isSource && !isCurrent && !isInTransitAfter && isPassed && (
                                <span className="text-[10px] text-green-600 font-medium">Departed</span>
                              )}
                              {station.isDestination && isPassed && (
                                <span className="text-[10px] text-green-600 font-medium">Arrived</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!loading && !error && stations.length === 0 && trackingData && (
              <div className="flex flex-col items-center justify-center py-20 text-center px-4">
                <MapPinIcon className="h-16 w-16 text-gray-300 mb-4" />
                <p className="text-gray-500">No station data available</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t bg-gray-50 px-4 py-3 flex justify-between items-center">
            <span className="text-xs text-gray-500">
              {stations.length > 0 && `${stations.length} stations${currentIndex >= 0 ? ` • ${currentIndex + 1} passed` : ''}`}
            </span>
            <button
              onClick={fetchTrackingData}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainTrackingModal;
